/**
 * PKMJournalTab — daily mood/habit/gratitude journal + streak counter.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Flame, Heart, Battery, Plus, X, BookOpen, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().slice(0, 10);

const MOOD_LABELS = { 1: '😞 Low', 2: '😕 Meh', 3: '😐 OK', 4: '🙂 Good', 5: '😄 Great' };
const ENERGY_LABELS = { 1: 'Drained', 2: 'Low', 3: 'Steady', 4: 'Energised', 5: 'Peak' };

const PKMJournalTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [entry, setEntry] = useState({ entry_date: todayISO(), mood: null, energy: null, gratitude: '', reflection: '', habits: [] });
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [habitInput, setHabitInput] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [t, s, h] = await Promise.all([
        axios.get(`${API}/pkm/journal/${todayISO()}`, { headers }),
        axios.get(`${API}/pkm/journal-stats/streak`, { headers }),
        axios.get(`${API}/pkm/journal?days=30`, { headers }),
      ]);
      setEntry({
        entry_date: todayISO(),
        mood: t.data.mood,
        energy: t.data.energy,
        gratitude: t.data.gratitude || '',
        reflection: t.data.reflection || '',
        habits: t.data.habits || [],
      });
      setStats(s.data);
      setHistory(h.data);
    } catch {
      toast.error('Failed to load journal');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pkm/journal`, entry, { headers });
      toast.success('Journal saved');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addHabit = () => {
    const h = habitInput.trim();
    if (h && !entry.habits.includes(h)) {
      setEntry({ ...entry, habits: [...entry.habits, h] });
      setHabitInput('');
    }
  };

  const removeHabit = (h) => setEntry({ ...entry, habits: entry.habits.filter(x => x !== h) });

  return (
    <div className="space-y-4" data-testid="pkm-journal-panel">
      {/* Streak summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-[#E5E7EB]" data-testid="streak-current">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-[#FF9500]/10 flex items-center justify-center"><Flame className="w-4 h-4 text-[#FF9500]" /></div>
              <div><p className="text-[10px] text-[#9CA3AF]">Current streak</p><p className="text-lg font-semibold">{stats?.current_streak ?? 0} <span className="text-xs font-normal text-[#9CA3AF]">days</span></p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-[#34C759]/10 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-[#34C759]" /></div>
              <div><p className="text-[10px] text-[#9CA3AF]">Longest streak</p><p className="text-lg font-semibold">{stats?.longest_streak ?? 0} <span className="text-xs font-normal text-[#9CA3AF]">days</span></p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-[#AF52DE]/10 flex items-center justify-center"><Heart className="w-4 h-4 text-[#AF52DE]" /></div>
              <div><p className="text-[10px] text-[#9CA3AF]">Avg mood (30d)</p><p className="text-lg font-semibold">{stats?.avg_mood_30d ? stats.avg_mood_30d.toFixed(1) : '—'} <span className="text-xs font-normal text-[#9CA3AF]">/5</span></p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-[#007AFF]/10 flex items-center justify-center"><BookOpen className="w-4 h-4 text-[#007AFF]" /></div>
              <div><p className="text-[10px] text-[#9CA3AF]">Total entries</p><p className="text-lg font-semibold">{stats?.total_entries ?? 0}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Today's entry editor */}
        <div className="lg:col-span-2">
          <Card className="border-[#E5E7EB]" data-testid="journal-editor">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Today · {todayISO()}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mood picker */}
              <div>
                <Label className="text-xs mb-2 block flex items-center gap-1"><Heart className="w-3 h-3 text-[#AF52DE]" />How are you feeling?</Label>
                <div className="flex gap-1.5 flex-wrap">
                  {[1, 2, 3, 4, 5].map(v => (
                    <Button
                      key={v}
                      size="sm"
                      variant={entry.mood === v ? 'default' : 'outline'}
                      className={`h-8 text-xs ${entry.mood === v ? 'bg-[#AF52DE] hover:bg-[#AF52DE]/90' : ''}`}
                      onClick={() => setEntry({ ...entry, mood: v })}
                      data-testid={`mood-${v}`}
                    >
                      {MOOD_LABELS[v]}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Energy */}
              <div>
                <Label className="text-xs mb-2 block flex items-center gap-1"><Battery className="w-3 h-3 text-[#34C759]" />Energy level</Label>
                <div className="flex gap-1.5 flex-wrap">
                  {[1, 2, 3, 4, 5].map(v => (
                    <Button
                      key={v}
                      size="sm"
                      variant={entry.energy === v ? 'default' : 'outline'}
                      className={`h-8 text-xs ${entry.energy === v ? 'bg-[#34C759] hover:bg-[#34C759]/90' : ''}`}
                      onClick={() => setEntry({ ...entry, energy: v })}
                      data-testid={`energy-${v}`}
                    >
                      {v} · {ENERGY_LABELS[v]}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Habits */}
              <div>
                <Label className="text-xs mb-2 block">Habits completed today</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add habit (e.g. read, exercise, meditate)"
                    value={habitInput}
                    onChange={e => setHabitInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHabit(); } }}
                    data-testid="habit-input"
                  />
                  <Button variant="outline" size="sm" onClick={addHabit} data-testid="add-habit-btn"><Plus className="w-3 h-3" /></Button>
                </div>
                {entry.habits.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {entry.habits.map(h => (
                      <Badge key={h} className="bg-[#34C759]/10 text-[#34C759] text-[10px] cursor-pointer" onClick={() => removeHabit(h)}>
                        {h} <X className="w-2.5 h-2.5 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div><Label className="text-xs">Gratitude</Label><Input value={entry.gratitude} onChange={e => setEntry({ ...entry, gratitude: e.target.value })} placeholder="One thing you're grateful for..." data-testid="gratitude-input" /></div>
              <div><Label className="text-xs">Reflection</Label><Textarea rows={3} value={entry.reflection} onChange={e => setEntry({ ...entry, reflection: e.target.value })} placeholder="Anything on your mind..." data-testid="reflection-input" /></div>

              <Button className="bg-[#002FA7] hover:bg-[#00227A] w-full" onClick={save} disabled={saving} data-testid="save-journal-btn">
                {saving ? 'Saving...' : 'Save entry'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right: history + top habits */}
        <div className="space-y-4">
          <Card className="border-[#E5E7EB]">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Top habits (30d)</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {!stats?.top_habits_30d?.length ? (
                <p className="text-xs text-[#9CA3AF]">No habits logged yet</p>
              ) : (
                <div className="space-y-1.5">
                  {stats.top_habits_30d.map(({ habit, count }) => (
                    <div key={habit} className="flex items-center gap-2 text-xs">
                      <span className="text-[#4B5563] truncate flex-1">{habit}</span>
                      <Badge variant="secondary" className="text-[10px]">{count}×</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-[#E5E7EB]">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Recent entries</CardTitle></CardHeader>
            <CardContent className="pt-0 space-y-1.5 max-h-[260px] overflow-y-auto">
              {history.length === 0 ? <p className="text-xs text-[#9CA3AF]">No entries yet</p> : history.slice(0, 14).map(e => (
                <div key={e.entry_date} className="flex items-center gap-2 text-xs p-1.5 border border-[#E5E7EB] rounded">
                  <span className="text-[#4B5563] font-medium">{e.entry_date}</span>
                  {e.mood && <span>{MOOD_LABELS[e.mood]?.split(' ')[0]}</span>}
                  {e.habits?.length > 0 && <Badge variant="secondary" className="text-[8px] ml-auto">{e.habits.length} habits</Badge>}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PKMJournalTab;
